import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { CardWorkExperience } from "../../../common/components/card/experience";
import { FormPage } from "../../../common/components/form_page"
import { textChooser } from "../../../common/util/textChooser";
import { useUser } from "../../../common/hooks/User";
import { createUserWorkExperience } from "../../../common/apis/private.api";
import { Field } from "../../../common/components/controls/field";
import { Areas } from "../../../common/components/controls/select/areas";
import { Positions } from "../../../common/components/controls/select/positions";
import { Sector } from "../../../common/components/controls/select/sector";
import { DatePickerControl } from "../../../common/components/controls/date_picker";
import { PhoneCode } from "../../../common/components/controls/select/phone_code";
import { InfoText } from "../../../common/components/info_text";
import { Item, ListItems } from "../../../common/components/list_items";
import { ButtonAdd } from "../../../common/components/controls/button/buttonAdd";
import { CheckBox } from "../../../common/components/controls/checkbox";

interface ExperienceProps { }

export const Experience = (props: ExperienceProps) => {
  const client = useQueryClient();
  const [isCurrentlyWork, setCurrentlyWork] = useState(false);
  const { getDataAll, refreshUserSession } = useUser()
  const { workExperience, id } = getDataAll();

  const {
    register, handleSubmit, setValue, reset, watch, resetField,
    setError, clearErrors, formState: { errors }
  } = useForm();

  const [startAt, finishAt] = watch(["startAt", "finishAt"]);

  const { isLoading, isError, isSuccess, mutate } = useMutation((params: any) =>
    createUserWorkExperience(params.id, params.body)
  );

  const handleOnSubmit = ({ codePhoneContactRef, inProgress, ...rest }: any) => {

    if (!inProgress && !rest.finishAt) {
      setError("finishAt", { type: "custom" });
      setError("inProgress", { type: "custom" });
      return;
    }

    rest.cellphoneContactRef = `${codePhoneContactRef}${rest.cellphoneContactRef}`;
    inProgress && delete rest.finishAt;

    const workExp = { ...rest, inProgress };

    mutate({ id, body: { workExperiences: [workExp] } });

    setCurrentlyWork(false);
    client.invalidateQueries(['validate-profile']);
  }

  useEffect(() => {
    if (isSuccess) {
      refreshUserSession();
      reset();
    }
  }, [isSuccess]);

  const buttonLabel = isLoading ? textChooser("txt140") : workExperience.length ? textChooser("txt209") : textChooser("txt215")

  return (
    <FormPage pageTitle={textChooser("txt78")} pageDescription={textChooser("txt100")}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <Field
              hooks={{ ...register("jobName", { required: true }) }}
              label={textChooser("txt101")}
              type={"text"}
              maxLength={199}
              hasError={false}
              hasIcon={false}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Areas
              hook={{ ...register("field", { required: true }) }}
              hasError={false}
              hasIcon={false}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <Positions
              onChange={(e) => setValue("hierarchy", e?.target.value ?? "")}
              hook={{ ...register("hierarchy", { required: true }) }}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Sector
              label={textChooser("txt216")}
              onChange={(e) => setValue("sector", e?.target.value ?? "")}
              hook={{ ...register("sector", { required: true }) }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <DatePickerControl
              maxDate={finishAt}
              onChange={(e) => setValue("startAt", e)}
              label={textChooser("txt105")}
              selected={startAt}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <DatePickerControl
              minDate={startAt}
              maxDate={new Date()}
              onChange={(date) => {
                setValue("finishAt", date);
                resetField("inProgress");
                clearErrors();
              }}
              label={textChooser("txt106")}
              selected={finishAt}
              disabled={isCurrentlyWork}
              hasError={!!errors.finishAt}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <CheckBox
              hook={{ ...register("inProgress") }}
              label={textChooser("txt107")}
              name={"inProgress"}
              className={"primary"}
              onChange={(e) => {
                setValue("inProgress", e?.target.checked);
                setValue('finishAt', new Date());
                setCurrentlyWork(!isCurrentlyWork);
                clearErrors();
              }}
              hasError={!!errors.inProgress}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <br /><b>{textChooser("txt108")}</b>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <Field
              hooks={{ ...register("nameContactRef") }}
              subLabel={textChooser("txt22")}
              type={"text"}
              maxLength={45}
              hasError={false}
              hasIcon={false}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Field
              type={"email"}
              hooks={{ ...register("emailContactRef") }}
              subLabel={textChooser("txt38")}
              maxLength={45}
              hasError={false}
              hasIcon={false}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4 col-md-2">
            <PhoneCode
              hook={{ ...register("codePhoneContactRef") }}
            />
          </div>
          <div className="col-sm-8 col-md-4">
            <Field
              hooks={{ ...register("cellphoneContactRef") }}
              subLabel={textChooser("txt32")}
              type={"tel"}
              maxLength={15}
              hasError={false}
              hasIcon={false}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <InfoText text={textChooser("txt141")} type="danger" hidden={!isError} />
          </div>
          <div className="col-xs-12" style={{ marginTop: "15px" }}>
            <ButtonAdd
              type="submit"
              label={buttonLabel}
              disabled={isLoading}
            />
          </div>
        </div>
      </form>

      <ListItems>
        {workExperience.map((experience) =>
          <Item key={experience.id}>
            <CardWorkExperience {...experience} />
          </Item>
        )}
      </ListItems>
    </FormPage>
  )
}